import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Image as ImageIcon,
  AccessTime,
  Block,
  ArrowForwardRounded,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getAllTopupRequests,
  getTopupRequestDetails,
  approveTopupRequest,
  rejectTopupRequest,
  getEvidencePhotoUrl,
} from 'api/wallet';
import AuthenticatedImage from 'components/AuthenticatedImage';
import notificationService from 'services/notificationService';
import BentoCard from 'components/bento/BentoCard';
import CustomerSearchAutocomplete from 'components/CustomerSearchAutocomplete';
import TransactionDetailDialog from './TransactionDetailDialog';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];

const getStatusColor = (s) => ({ approved: 'success', rejected: 'error', pending: 'warning', cancelled: 'default' }[s] || 'default');
const getStatusIcon = (s) => ({ approved: <CheckCircle />, rejected: <Cancel />, pending: <AccessTime />, cancelled: <Block /> }[s] || null);

const METHOD_LABEL = { cash_store: 'Cash at store', bank_transfer: 'Bank transfer', staff_advance: 'Staff advance' };
const SYM = { THB: '฿', LAK: '₭', USD: '$' };
const fmtAmt = (n, c) => `${SYM[c] || ''}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
// Stored rate convention (backend createSplitCashPayment): USD = THB per 1 USD, LAK = LAK per 1 THB.
const rateText = (currency, rate) => {
  if (!rate) return '';
  const r = Number(rate).toLocaleString(undefined, { maximumFractionDigits: 4 });
  return currency === 'USD' ? `1 USD = ฿${r}` : currency === 'LAK' ? `1 THB = ₭${r}` : '';
};
// What the customer actually handed over. New-shape requests carry `lines` (each with its
// own currency, stored rate and THB value); older single-source ones only have
// receivedAmount/receivedCurrency (no rate was captured then).
const topupPayLines = (d) => {
  if (!d) return [];
  if (d.lines?.length) return d.lines;
  if (d.paymentSource) return [{ method: d.paymentSource, currency: d.receivedCurrency || d.currency, amount: d.receivedAmount ?? d.amount }];
  return [];
};
const linePhotoFiles = (line) => (line.evidencePhotos?.length ? line.evidencePhotos : (line.evidencePhoto ? [line.evidencePhoto] : [])).map((p) => p?.filename).filter(Boolean);

// Mobile-card visual tokens — match the bespoke card pattern used by ShippingApprovals
// (scenes/financial/index.jsx) so the two Approvals sub-tabs look consistent.
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const CARD_SHADOW = '0 1px 2px rgba(20,18,30,.04),0 16px 32px -24px rgba(20,18,30,.18)';
const initials = (name) => {
  const p = (name || '?').trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : (name || '?').slice(0, 2)).toUpperCase();
};

// Top-up request approval, ported from scenes/wallet/approval.jsx to live as a sub-tab
// of the standalone Approvals page (scenes/financial/approvals.jsx). Same logic/dialogs
// minus the standalone PageShell + header nav buttons. `onChanged` lets the parent
// refresh its badge counts after an approve/reject.
const TopupApprovalsTab = ({ onChanged }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  // Pending first by default; a deep-link (e.g. from a notification) can preselect a status.
  const [statusFilter, setStatusFilter] = useState(() => location.state?.statusFilter || 'pending');
  const [customer, setCustomer] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [detailsDialog, setDetailsDialog] = useState(false);
  const [imageDialog, setImageDialog] = useState(false);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDetails, setRequestDetails] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [txId, setTxId] = useState(null); // ledger transaction shown in a nested dialog

  const loadRequests = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const result = await getAllTopupRequests(page, 20, statusFilter, '', dateFrom, dateTo, customer?._id || '');
      setRequests(result.data.requests);
      setPagination(result.data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, customer, dateFrom, dateTo]);

  useEffect(() => {
    setCurrentPage(1);
    loadRequests(1);
  }, [statusFilter, customer, dateFrom, dateTo, loadRequests]);

  useEffect(() => {
    // The notification feed is polled (no socket); reload whenever it refreshes so
    // new top-up requests show up automatically while viewing the pending list.
    const unsubscribe = notificationService.subscribe('notifications-loaded', () => {
      loadRequests(currentPage);
    });
    return unsubscribe;
  }, [currentPage, loadRequests]);

  const loadRequestDetails = async (requestId) => {
    try {
      const result = await getTopupRequestDetails(requestId);
      setRequestDetails(result.data);
    } catch (err) {
      setError(err.message || 'Failed to load request details');
    }
  };

  const formatCurrency = (amount, currency) => {
    const symbols = { USD: '$', THB: '฿', LAK: '₭' };
    return `${symbols[currency] || ''}${(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  const handleViewDetails = async (request) => {
    setSelectedRequest(request);
    setRequestDetails(null);
    await loadRequestDetails(request._id);
    setDetailsDialog(true);
  };

  const handleViewImage = (request) => {
    setSelectedRequest(request);
    setImageDialog(true);
  };

  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    setApprovalNotes('');
    setApproveDialog(true);
  };

  const handleRejectClick = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectDialog(true);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    try {
      setActionLoading(true);
      setError('');
      await approveTopupRequest(selectedRequest._id, approvalNotes);
      setSuccess(`Top-up request ${selectedRequest._id} has been approved successfully`);
      setApproveDialog(false);
      await loadRequests(currentPage);
      onChanged && onChanged();
    } catch (err) {
      setError(err.message || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      setError('Rejection reason is required');
      return;
    }
    try {
      setActionLoading(true);
      setError('');
      await rejectTopupRequest(selectedRequest._id, rejectionReason);
      setSuccess(`Top-up request ${selectedRequest._id} has been rejected`);
      setRejectDialog(false);
      await loadRequests(currentPage);
      onChanged && onChanged();
    } catch (err) {
      setError(err.message || 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    loadRequests(page);
  };

  const isPending = (r) => r?.status === 'pending';

  if (loading && currentPage === 1 && requests.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Filters: status (default Pending) + customer autocomplete + date range — same
          responsive grid pattern as ShippingApprovals (no Paper/BentoCard wrapper). */}
      <Box
        sx={{
          display: { xs: 'grid', md: 'flex' },
          gridTemplateColumns: { xs: '1fr 1fr' },
          alignItems: 'center',
          gap: '12px',
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ gridColumn: { xs: '1 / -1' }, width: { xs: '100%', md: 'auto' }, minWidth: { md: 220 }, flex: { md: '1 1 220px' } }}>
          <CustomerSearchAutocomplete value={customer} onChange={setCustomer} label={t('financial.customer')} size="small" />
        </Box>
        <FormControl size="small" sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 160 } }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small" type="datetime-local" label="From" sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 200 } }}
          value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small" type="datetime-local" label="To" sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 200 } }}
          value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined" color="secondary"
          sx={{ width: { xs: '100%', md: 'auto' } }}
          onClick={() => { setCustomer(null); setDateFrom(''); setDateTo(''); }}
        >
          Clear
        </Button>
        <Box sx={{ gridColumn: { xs: '1 / -1' }, ml: { md: 'auto' }, textAlign: { xs: 'right' }, fontSize: '12.5px', color: '#a8a3b4', fontWeight: 600 }}>
          {pagination.total || 0} results
        </Box>
      </Box>

      {requests.length === 0 ? (
        <BentoCard bodySx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {statusFilter === 'pending' ? 'All caught up!' : 'No requests found'}
          </Typography>
          {statusFilter === 'pending' && (
            <Typography variant="body2" color="text.secondary">
              No pending top-up requests require your attention.
            </Typography>
          )}
        </BentoCard>
      ) : (
        <>
          {isMobile ? (
            /* ── Mobile: bespoke card — same visual pattern as ShippingApprovals
               (scenes/financial/index.jsx): hero amount + status chip, avatar +
               customer, 2x2 meta tiles, "View details" footer cue. Clicking the
               card opens the details dialog (approve/reject live there, like Shipping). */
            <Grid container spacing={2}>
              {requests.map((request) => (
                <Grid item xs={12} key={request._id}>
                  <Card
                    elevation={0}
                    onClick={() => handleViewDetails(request)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '20px',
                      border: '1px solid #ece9f1',
                      boxShadow: CARD_SHADOW,
                      transition: 'transform .18s, box-shadow .18s, border-color .18s',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        borderColor: '#d9cffb',
                        boxShadow: '0 1px 2px rgba(20,18,30,.05),0 22px 40px -22px rgba(86,40,160,.4)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      {/* header: amount + status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box>
                          <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: '#a8a3b4' }}>
                            Amount
                          </Typography>
                          <Typography sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 28, lineHeight: 1.15, color: '#6d28d9' }}>
                            {formatCurrency(request.amount, request.currency)}
                          </Typography>
                        </Box>
                        <Chip icon={getStatusIcon(request.status)} label={request.status} color={getStatusColor(request.status)} size="small" sx={{ fontWeight: 700, flexShrink: 0, textTransform: 'capitalize' }} />
                      </Box>

                      {/* customer */}
                      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: MONO, fontWeight: 700, fontSize: 12, background: 'linear-gradient(135deg,#7c3aed,#5b21b6)' }}>
                          {initials(request.customer?.name)}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#2c2937', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {request.customer?.name || request.customerId}
                          </Typography>
                          {request.customer?.whatsapp && (
                            <Typography sx={{ fontFamily: MONO, fontSize: 12, color: '#6b6775' }}>
                              {request.customer.whatsapp}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* meta tiles */}
                      <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                        {[
                          { label: 'Account', value: request.account?.id || '—' },
                          { label: 'Submitted', value: formatDate(request.createdAt) },
                          { label: 'Source', value: request.paymentSource ? request.paymentSource.replace(/_/g, ' ') : '—', capitalize: true },
                          { label: 'Staff', value: request.createdByStaff?.name || '—' },
                          ...(request.station ? [{ label: 'Station', value: request.station.name }] : []),
                        ].map((m, i) => (
                          <Box key={i} sx={{ p: 1.25, borderRadius: '12px', background: '#faf9fc', border: '1px solid #f0eef5' }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#a8a3b4' }}>
                              {m.label}
                            </Typography>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#2c2937', mt: 0.25, wordBreak: 'break-word', textTransform: m.capitalize ? 'capitalize' : 'none' }}>
                              {m.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* footer cue */}
                      <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #f1eff5', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, color: '#6d28d9' }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>View details</Typography>
                        <ArrowForwardRounded sx={{ fontSize: 16 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            /* ── Desktop: Table ── */
            <BentoCard bodySx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Request ID</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Station</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Evidence</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request._id} hover>
                        <TableCell>
                          <Tooltip title={request._id}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>...{request._id?.slice(-10)}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight="bold">{request.customer?.name || request.customerId}</Typography>
                          <Typography variant="body2" color="text.secondary">{request.customer?.whatsapp}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight="bold">{formatCurrency(request.amount, request.currency)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip icon={getStatusIcon(request.status)} label={request.status} color={getStatusColor(request.status)} size="small" sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                        <TableCell>
                          {request.paymentSource ? (
                            <Chip size="small" label={request.paymentSource.replace(/_/g, ' ')} variant="outlined" sx={{ textTransform: 'capitalize' }} />
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                          {request.createdByStaff && (
                            <Typography variant="caption" color="text.secondary" display="block">{request.createdByStaff.name}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{request.station?.name || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(request.createdAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          {request.evidencePhoto ? (
                            <Tooltip title="View Evidence Photo">
                              <IconButton size="small" onClick={() => handleViewImage(request)}><ImageIcon /></IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <Button variant="outlined" color="secondary" size="small" startIcon={<Visibility />} onClick={() => handleViewDetails(request)}>Details</Button>
                            {isPending(request) && (
                              <>
                                <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => handleApproveClick(request)}>Approve</Button>
                                <Button size="small" variant="contained" color="error" startIcon={<Cancel />} onClick={() => handleRejectClick(request)}>Reject</Button>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </BentoCard>
          )}

          {pagination.pages > 1 && (
            <Box display="flex" justifyContent="center" pt={3}>
              <Pagination count={pagination.pages} page={currentPage} onChange={handlePageChange} color="secondary" />
            </Box>
          )}
        </>
      )}

      {/* Request Details Dialog */}
      <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Request Details - {selectedRequest?._id}</DialogTitle>
        <DialogContent>
          {requestDetails && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Customer Information</Typography>
                    <Typography variant="body2" color="text.secondary">Name: {requestDetails.customer?.name}</Typography>
                    <Typography variant="body2" color="text.secondary">WhatsApp: {requestDetails.customer?.whatsapp}</Typography>
                    <Typography variant="body2" color="text.secondary">Account: {requestDetails.account?.id || 'N/A'}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Request Information</Typography>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Status:</Typography>
                      <Chip icon={getStatusIcon(requestDetails.status)} label={requestDetails.status} color={getStatusColor(requestDetails.status)} size="small" sx={{ textTransform: 'capitalize' }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">Amount: {formatCurrency(requestDetails.amount, requestDetails.currency)}</Typography>
                    <Typography variant="body2" color="text.secondary">Currency: {requestDetails.currency}</Typography>
                    {(() => {
                      const pl = topupPayLines(requestDetails);
                      if (!pl.length || !(pl.length > 1 || pl.some((l) => l.currency !== requestDetails.currency))) return null;
                      const summary = pl
                        .map((l) => `${fmtAmt(l.amount, l.currency)}${l.currency !== requestDetails.currency && l.exchangeRate ? ` (@ ${rateText(l.currency, l.exchangeRate)})` : ''}`)
                        .join(' + ');
                      return <Typography variant="body2" sx={{ fontWeight: 700 }}>Paid with: {summary}</Typography>;
                    })()}
                    {requestDetails.station && (
                      <Typography variant="body2" color="text.secondary">Station: {requestDetails.station.name}</Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">Submitted: {formatDate(requestDetails.createdAt)}</Typography>
                    {requestDetails.reviewedBy && (
                      <Typography variant="body2" color="text.secondary">Reviewed by: {requestDetails.reviewedBy.name} ({formatDate(requestDetails.reviewedBy.reviewedAt)})</Typography>
                    )}
                    {requestDetails.approvalNotes && <Typography variant="body2" color="text.secondary">Notes: {requestDetails.approvalNotes}</Typography>}
                    {requestDetails.rejectionReason && <Typography variant="body2" color="error">Rejection reason: {requestDetails.rejectionReason}</Typography>}
                    {requestDetails.transactionId && (
                      <Button variant="outlined" color="secondary" size="small" sx={{ mt: 1.5 }} onClick={() => setTxId(requestDetails.transactionId)}>
                        View Transaction
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              {topupPayLines(requestDetails).length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>Payment received</Typography>
                      {topupPayLines(requestDetails).map((l, i) => {
                        const foreign = l.currency && l.currency !== 'THB';
                        const note = foreign
                          ? [rateText(l.currency, l.exchangeRate) && `@ ${rateText(l.currency, l.exchangeRate)}`, l.amountTHB ? `≈ ${fmtAmt(l.amountTHB, 'THB')}` : ''].filter(Boolean).join(' · ')
                          : '';
                        return (
                          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 0.75, borderTop: i ? '1px solid rgba(0,0,0,0.08)' : 'none' }}>
                            <Box>
                              <Typography variant="body1" fontWeight="bold">{METHOD_LABEL[l.method] || l.method}</Typography>
                              {note && <Typography variant="body2" color="text.secondary">{note}</Typography>}
                            </Box>
                            <Typography variant="body1" fontWeight="bold">{fmtAmt(l.amount, l.currency)} {l.currency}</Typography>
                          </Box>
                        );
                      })}
                      {requestDetails.changeLines?.length > 0 && (
                        <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Change given</Typography>
                          {requestDetails.changeLines.map((l, i) => (
                            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">{l.source === 'wallet_credit' ? 'Wallet credit' : 'Cash'}</Typography>
                              <Typography variant="body2" color="error" fontWeight="bold">-{fmtAmt(l.amount, l.currency)} {l.currency}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Description</Typography>
                    <Typography variant="body1">{requestDetails.description || 'No description provided'}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              {(() => {
                // Split-payment requests keep their evidence on each line; the top-level
                // evidencePhoto only exists for single-source ones.
                const files = requestDetails.lines?.length
                  ? requestDetails.lines.flatMap(linePhotoFiles)
                  : (requestDetails.evidencePhoto ? [requestDetails.evidencePhoto.filename] : []);
                if (!files.length) return null;
                return (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>Evidence Photo{files.length > 1 ? `s (${files.length})` : ''}</Typography>
                        {files.map((f) => (
                          <AuthenticatedImage
                            key={f}
                            src={getEvidencePhotoUrl(f)}
                            alt="Evidence"
                            style={{ width: '100%', height: 'auto', maxHeight: 400, objectFit: 'contain', border: '1px solid #e0e0e0', borderRadius: '4px', marginBottom: 8 }}
                          />
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })()}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {isPending(selectedRequest) && (
            <>
              <Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => { setDetailsDialog(false); handleApproveClick(selectedRequest); }}>Approve</Button>
              <Button variant="contained" color="error" startIcon={<Cancel />} onClick={() => { setDetailsDialog(false); handleRejectClick(selectedRequest); }}>Reject</Button>
            </>
          )}
          <Button onClick={() => setDetailsDialog(false)} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Evidence Image Dialog */}
      <Dialog open={imageDialog} onClose={() => setImageDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Evidence Photo - {selectedRequest?._id}</DialogTitle>
        <DialogContent>
          {selectedRequest?.evidencePhoto && (
            <AuthenticatedImage
              src={getEvidencePhotoUrl(selectedRequest.evidencePhoto.filename)}
              alt="Evidence"
              style={{ width: '100%', height: 'auto', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialog(false)} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialog} onClose={() => setApproveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckCircle color="success" sx={{ mr: 1 }} />
          Approve Top-up Request
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>Are you sure you want to approve this top-up request?</Typography>
          {selectedRequest && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">Request: {selectedRequest._id}</Typography>
              <Typography variant="body2" color="text.secondary">Amount: {formatCurrency(selectedRequest.amount, selectedRequest.currency)}</Typography>
              <Typography variant="body2" color="text.secondary">Customer: {selectedRequest.customer?.name}</Typography>
            </Box>
          )}
          <TextField fullWidth label="Approval Notes (Optional)" multiline rows={3} value={approvalNotes} onChange={(e) => setApprovalNotes(e.target.value)} placeholder="Add any notes about this approval..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(false)} variant="outlined" color="secondary">Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={actionLoading} startIcon={actionLoading ? <CircularProgress size={20} /> : <CheckCircle />}>
            {actionLoading ? 'Approving...' : 'Approve Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Cancel color="error" sx={{ mr: 1 }} />
          Reject Top-up Request
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>Please provide a reason for rejecting this top-up request:</Typography>
          {selectedRequest && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">Request: {selectedRequest._id}</Typography>
              <Typography variant="body2" color="text.secondary">Amount: {formatCurrency(selectedRequest.amount, selectedRequest.currency)}</Typography>
              <Typography variant="body2" color="text.secondary">Customer: {selectedRequest.customer?.name}</Typography>
            </Box>
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Rejection Reason</InputLabel>
            <Select
              value={['Insufficient evidence', 'Invalid payment proof', 'Amount mismatch', 'Duplicate request'].includes(rejectionReason) ? rejectionReason : 'Other'}
              onChange={(e) => {
                if (e.target.value !== 'Other') setRejectionReason(e.target.value);
                else setRejectionReason('');
              }}
              label="Rejection Reason"
            >
              <MenuItem value="Insufficient evidence">Insufficient evidence</MenuItem>
              <MenuItem value="Invalid payment proof">Invalid payment proof</MenuItem>
              <MenuItem value="Amount mismatch">Amount mismatch</MenuItem>
              <MenuItem value="Duplicate request">Duplicate request</MenuItem>
              <MenuItem value="Other">Other (specify below)</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="Details / Custom Reason" multiline rows={3} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Provide detailed reason for rejection..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} variant="outlined" color="secondary">Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading || !rejectionReason.trim()} startIcon={actionLoading ? <CircularProgress size={20} /> : <Cancel />}>
            {actionLoading ? 'Rejecting...' : 'Reject Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <TransactionDetailDialog open={!!txId} txId={txId} onClose={() => setTxId(null)} />
    </Box>
  );
};

export default TopupApprovalsTab;
